import { Chip, ChipProps } from '@mui/material';
import * as Types from 'gql-types';
import { ReactElement, useMemo } from 'react';

import { NO_OUTCOME_CODE } from '../../constants/visit';
import { Outcome, Priority, Status } from '../../fetching/sharedSchemas/visits';
import {
    ClinicianAppointmentState,
    ClinicianAppointmentStateColors,
    renderClinicianAppointmentState,
} from '../../types/clinicianAppointment';
import {
    VisitOutcome,
    VisitOutcomeColors,
    VisitPriority,
    VisitPriorityColors,
    VisitPriorityOrder,
    VisitState,
    VisitStateColors,
    renderVisitOutcome,
    renderVisitPriority,
    renderVisitState,
} from '../../types/visit';
import { makeDarker, makeTranslucent } from '../../utils/colors';
import { Style } from '../../utils/reactTypes';
import { AxleChip } from '../displayFields/chips';
import { ServicePriorityIcon } from '../icons/PriorityIcon';

export const StatusOrOutcomeChip = ({
    label,
    color,
    onClick,
    style,
    icon,
    ...rest
}: {
    label: string;
    color: string;
    onClick?: () => void;
    style?: Style;
    icon?: ReactElement;
} & Omit<ChipProps, 'label' | 'color'>) => {
    // Stable per (color, style) so Emotion doesn't re-serialize chip styles on
    // every table render pass.
    const sx = useMemo(
        () => ({
            border: `1px solid ${color}`,
            color: color,
            margin: 0.5,
            maxWidth: '200px',
            ...style,
        }),
        [color, style],
    );
    return (
        <Chip
            icon={icon}
            label={label}
            onClick={onClick}
            variant='outlined'
            size='small'
            sx={sx}
            {...rest}
        />
    );
};

export const VisitStateChip = ({
    state,
    onClick,
}: {
    state: VisitState | Types.VisitState;
    onClick?: () => void;
}) => (
    <StatusOrOutcomeChip
        label={renderVisitState(state)}
        color={VisitStateColors[state]}
        onClick={onClick}
    />
);

export const AppointmentStateChip = ({
    state,
    onClick,
}: {
    state: ClinicianAppointmentState;
    onClick?: () => void;
}) => (
    <StatusOrOutcomeChip
        label={renderClinicianAppointmentState(state)}
        color={ClinicianAppointmentStateColors[state]}
        onClick={onClick}
    />
);

export const VisitOutcomeChip = ({
    outcome,
    showNone = true,
    onClick,
}: {
    outcome: VisitOutcome;
    showNone?: boolean;
    onClick?: () => void;
}) =>
    !showNone && outcome === VisitOutcome.NONE ? null : (
        <StatusOrOutcomeChip
            label={renderVisitOutcome(outcome)}
            color={VisitOutcomeColors[outcome]}
            onClick={onClick}
        />
    );

export const StatusChip = ({ status, onClick }: { status: Status; onClick?: () => void }) => (
    <StatusOrOutcomeChip label={status.name} color={status.color} onClick={onClick} />
);

export const OutcomeChip = ({
    outcome,
    showNone = true,
    onClick,
}: {
    outcome: Outcome;
    showNone?: boolean;
    onClick?: () => void;
}) =>
    !showNone && outcome.code === NO_OUTCOME_CODE ? null : (
        <StatusOrOutcomeChip label={outcome.name} color={outcome.color} onClick={onClick} />
    );

export const PriorityChip = ({
    priority,
    onClick,
    style,
}: {
    priority: Priority;
    onClick?: () => void;
    style?: Style;
}) => {
    const sx = useMemo(
        () => ({
            backgroundColor: makeTranslucent(priority.color, 0.75),
            color: makeDarker(priority.color, 50),
            borderWidth: 0,
            height: '2rem',
            width: '7rem',
            ...style,
        }),
        [priority.color, style],
    );
    return (
        <AxleChip
            label={priority.name}
            icon={ServicePriorityIcon({ priority: priority.order })}
            onClick={onClick}
            sx={sx}
        />
    );
};

export const VisitPriorityChipGql = ({
    priority,
    onClick,
    style,
}: {
    priority: VisitPriority;
    onClick?: () => void;
    style?: Style;
}) => {
    const sx = useMemo(
        () => ({
            backgroundColor: makeTranslucent(VisitPriorityColors[priority], 0.75),
            color: makeDarker(VisitPriorityColors[priority], 50),
            borderWidth: 0,
            height: '2rem',
            width: '7rem',
            ...style,
        }),
        [priority, style],
    );
    return (
        <AxleChip
            label={renderVisitPriority(priority)}
            icon={ServicePriorityIcon({ priority: VisitPriorityOrder[priority] })}
            onClick={onClick}
            sx={sx}
        />
    );
};

export const RequestedVisitStateChip = ({ onClick }: { onClick?: () => void }) => (
    <StatusOrOutcomeChip
        label='Requested'
        color={VisitStateColors[VisitState.UNSCHEDULED]}
        onClick={onClick}
    />
);
